/**
 * Toast system — lightweight notification stack.
 *
 * API:
 *   1. Mount `<ToastProvider />` once in AppShell (wraps nothing, just registers the portal).
 *   2. Call `useToast()` anywhere to get `toast({ kind, message })`.
 *   3. Toasts spring in from the bottom-right, auto-dismiss after 4 s, and stack.
 *   4. Reduced-motion: toasts appear/disappear instantly.
 */
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { springTransition, instantTransition, useMotionPreference } from '../../lib/motion';

// ── Types ────────────────────────────────────────────────────────────────────

export type ToastKind = 'ok' | 'warn' | 'bad' | 'info';

export interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
}

export interface ToastOptions {
  kind: ToastKind;
  message: string;
  /** Auto-dismiss delay in ms. Default 4000. */
  duration?: number;
}

// ── Context ──────────────────────────────────────────────────────────────────

type ToastFn = (opts: ToastOptions) => void;
const ToastContext = createContext<ToastFn | null>(null);

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns a `toast(opts)` function that queues a notification.
 * Must be used inside a `<ToastProvider>`.
 */
export function useToast(): ToastFn {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// ── Styling ──────────────────────────────────────────────────────────────────

const KIND_CLASSES: Record<ToastKind, string> = {
  ok:   'ring-ok/40 text-ok',
  warn: 'ring-warn/40 text-warn',
  bad:  'ring-bad/40 text-bad',
  info: 'ring-accent-cyan/40 text-accent-cyan',
};

const KIND_ICONS: Record<ToastKind, string> = {
  ok:   '✓',
  warn: '⚠',
  bad:  '✕',
  info: 'ℹ',
};

// ── Individual Toast ─────────────────────────────────────────────────────────

interface ToastCardProps {
  item: ToastItem;
  onDismiss: (id: string) => void;
}

function ToastCard({ item, onDismiss }: ToastCardProps) {
  const pref = useMotionPreference();
  const transition = pref === 'full' ? springTransition : instantTransition;

  return (
    <motion.li
      layout
      key={item.id}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.95 }}
      transition={transition}
      role="status"
      aria-live="polite"
      className={clsx(
        'flex items-start gap-2.5 rounded-xl px-4 py-3',
        'backdrop-blur-lg bg-bg-elevated',
        'ring-1',
        'shadow-glass',
        'min-w-[260px] max-w-[380px]',
        KIND_CLASSES[item.kind],
      )}
    >
      <span className="mt-px text-sm font-bold leading-none" aria-hidden="true">
        {KIND_ICONS[item.kind]}
      </span>
      <span className="flex-1 text-sm text-text-1">{item.message}</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(item.id)}
        className="mt-px shrink-0 rounded p-0.5 text-text-3 transition-colors hover:text-text-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <line x1="2" y1="2" x2="10" y2="10" />
          <line x1="10" y1="2" x2="2" y2="10" />
        </svg>
      </button>
    </motion.li>
  );
}

// ── Provider ─────────────────────────────────────────────────────────────────

/**
 * Mount once in AppShell. Provides `useToast()` to the entire component tree
 * and renders active toasts in a portal to `document.body`.
 */
export function ToastProvider({ children }: { children?: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const baseId = useId();
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ kind, message, duration = 4000 }: ToastOptions) => {
      const id = `${baseId}-${++counterRef.current}`;
      setToasts((prev) => [...prev, { id, kind, message }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [baseId, dismiss],
  );

  const portal = createPortal(
    <ul
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <ToastCard item={t} onDismiss={dismiss} />
          </div>
        ))}
      </AnimatePresence>
    </ul>,
    document.body,
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {portal}
    </ToastContext.Provider>
  );
}
